import { Appearance, DraftConfiguration, DraftState, ProductType, View } from '@/types';
import { getViewFromConfiguration } from '@/model/configuration/getViewFromConfiguration';
import { getConfigurations } from '@/model/draft/getConfigurations';
import { getSelectedSizes } from '@/model/draft/getSelectedSizes';
import { getSortedConfigurationsOnView } from '@/model/draft/getSortedConfigurationsOnView';
import { getViewId } from '@/model/printArea/getViewId';
import { getDefaultView } from '@/model/productType/getDefaultView';
import { isSizeAvailable } from '@/model/productType/isSizeAvailable';
import { moveConfigurationsToProductType } from './helper/moveConfigurationsToProductType';
import { removeConfiguration } from './helper/removeConfiguration';

function hasViewAnyConfigurations(
  productType: ProductType,
  currentView: View,
  configurations: DraftConfiguration[]
) {
  const printAreaIdsOnView: string[] = [];
  for (const printArea of productType.printAreas) {
    if (getViewId(printArea) === currentView.id) {
      printAreaIdsOnView.push(printArea.id);
    }
  }

  return configurations.some((configuration) =>
    printAreaIdsOnView.includes(configuration.printArea.id)
  );
}

function getNextView(
  productType: ProductType,
  currentView: View,
  configurations: DraftConfiguration[],
  lastConfigurationFromPreviousView?: DraftConfiguration
) {
  const nextView = productType.views.find((v) => v.id === currentView.id);
  if (
    nextView && // if the current view exists on the new product type
    (!lastConfigurationFromPreviousView || // and we had no configurations on the view (or removed all of them)
      hasViewAnyConfigurations(productType, currentView, configurations)) // or at least one configuration on the view
  ) {
    // then stay on the current view
    return nextView;
  }

  if (lastConfigurationFromPreviousView) {
    // switch to the view that contains the newest design from the previous view
    return getViewFromConfiguration(productType, lastConfigurationFromPreviousView);
  }

  const defaultView = getDefaultView(productType);
  if (hasViewAnyConfigurations(productType, defaultView, configurations)) {
    // switch to the default view if it contains any design
    return defaultView;
  }

  const configurationCount = configurations.length;
  if (configurationCount) {
    // switch to the view that contains the newest design
    return getViewFromConfiguration(productType, configurations[configurationCount - 1]);
  }

  return defaultView;
}

function matchSizes(
  productType: ProductType,
  appearance: Appearance,
  selectedSizes: DraftState['selectedSizes']
) {
  const matchedSizes: DraftState['selectedSizes'] = {};
  Object.entries(selectedSizes).forEach(([sizeId, itemCount]) => {
    if (itemCount > 0 && isSizeAvailable(productType, appearance, sizeId)) {
      matchedSizes[sizeId] = itemCount;
    }
  });
  return matchedSizes;
}

export const switchProductTypeAction =
  (nextProductType: ProductType, appearance: Appearance, sizeId?: string) =>
  (draft: DraftState) => {
    const { productType: currentProductType, view, configurationIds } = draft;
    const configurations = getConfigurations(draft);
    const visibleConfigurations = getSortedConfigurationsOnView(
      configurations,
      configurationIds,
      view
    );

    const removeConfigurationIds = moveConfigurationsToProductType(
      configurations,
      currentProductType,
      nextProductType,
      appearance
    );
    removeConfigurationIds.forEach((configurationId) =>
      removeConfiguration(draft, configurationId)
    );

    const lastAcceptedConfigurationFromPreviousView = visibleConfigurations
      .filter(({ cid }) => !removeConfigurationIds.includes(cid))
      .pop();

    draft.productType = nextProductType;
    draft.appearance = appearance;
    draft.selectedSizes = sizeId
      ? { [sizeId]: 1 }
      : matchSizes(nextProductType, appearance, getSelectedSizes(draft));

    draft.view = getNextView(
      nextProductType,
      view,
      getConfigurations(draft), // new configurations array in case we removed any
      lastAcceptedConfigurationFromPreviousView
    );

    if (nextProductType.sizes.length === 1) {
      draft.selectedSizes = { [nextProductType.sizes[0].id]: 1 };
    }
  };
